import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Search, Loader2, X, Check, Image as ImageIcon } from 'lucide-react';

/**
 * Compact roof design template picker. Renders an inline thumbnail of the
 * currently-selected template (if any) with a "Change…" button that opens
 * a searchable modal of the full library.
 *
 * Props:
 *   value          — currently selected template _id (string | null)
 *   selectedSnap   — optional { name, thumbnail_url } shortcut so the inline
 *                    chip can render without a separate fetch round-trip
 *   onChange       — (templateId, templateSummary) => void
 *   compact        — render a small chip (default) vs. full block
 */
export default function RoofDesignTemplatePicker({ value, selectedSnap, onChange, compact = true }) {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [snap, setSnap] = useState(selectedSnap || null);

  // Resolve the selected snapshot if only an ID was provided
  useEffect(() => {
    if (!value) { setSnap(null); return; }
    if (selectedSnap && selectedSnap._id === value) { setSnap(selectedSnap); return; }
    (async () => {
      try {
        const { data } = await api.get(`/roof-templates/${value}`);
        setSnap({ _id: data._id, name: data.name, thumbnail_url: `/api/roof-templates/${data._id}/image` });
      } catch { setSnap(null); }
    })();
  }, [api, value, selectedSnap]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit: 60, search });
      const { data } = await api.get(`/roof-templates?${qs.toString()}`);
      setItems(data.items || []);
    } catch { setItems([]); }
    setLoading(false);
  }, [api, search]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const pick = (t) => {
    onChange?.(t._id, { _id: t._id, name: t.name, thumbnail_url: t.thumbnail_url });
    setSnap({ _id: t._id, name: t.name, thumbnail_url: t.thumbnail_url });
    setOpen(false);
  };

  const clear = () => {
    onChange?.('', null);
    setSnap(null);
  };

  return (
    <div data-testid="roof-tpl-picker" className={compact ? 'inline-flex items-center gap-2' : 'space-y-2'}>
      {snap ? (
        <div className="inline-flex items-center gap-2 px-2 py-1 bg-white border border-slate-200 rounded-md">
          <img src={snap.thumbnail_url} alt={snap.name} className="w-10 h-7 object-contain bg-slate-50 rounded" />
          <span className="text-xs font-semibold text-slate-700 max-w-[140px] truncate" title={snap.name}>{snap.name}</span>
          <button data-testid="roof-tpl-change" type="button" onClick={() => setOpen(true)} className="text-[10px] px-1.5 py-0.5 text-[#D35400] hover:underline font-semibold">Change</button>
          <button data-testid="roof-tpl-clear" type="button" onClick={clear} className="text-slate-400 hover:text-red-500"><X size={12} /></button>
        </div>
      ) : (
        <button
          data-testid="roof-tpl-pick"
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-dashed border-slate-300 text-slate-500 hover:border-[#D35400] hover:text-[#D35400] rounded-md"
        >
          <ImageIcon size={12} /> Pick roof design template…
        </button>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div data-testid="roof-tpl-picker-modal" className="bg-white rounded-xl w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 bg-slate-50">
              <h3 className="text-sm font-bold text-slate-800">Pick a roof design template</h3>
              <div className="ml-auto relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="roof-tpl-modal-search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="pl-7 pr-3 py-1.5 border border-slate-200 rounded-md text-xs w-56 focus:border-[#D35400] focus:outline-none"
                />
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </header>

            <div className="flex-1 overflow-y-auto p-3">
              {loading ? (
                <div className="text-center py-12 text-slate-400 text-sm flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
              ) : items.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-sm">No templates match.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {items.map(t => {
                    const active = value === t._id;
                    return (
                      <button
                        key={t._id}
                        data-testid={`roof-tpl-option-${t._id}`}
                        type="button"
                        onClick={() => pick(t)}
                        className={`group relative bg-white border rounded-md overflow-hidden text-left hover:shadow-md hover:shadow-orange-500/10 transition-all ${active ? 'border-[#D35400] ring-2 ring-orange-200' : 'border-slate-200 hover:border-[#D35400]'}`}
                      >
                        <div className="aspect-[4/3] bg-slate-50 flex items-center justify-center">
                          <img src={t.thumbnail_url} alt={t.name} loading="lazy" className="max-w-full max-h-full object-contain" />
                        </div>
                        <div className="px-2 py-1.5 border-t border-slate-100">
                          <p className="text-[11px] font-semibold text-slate-700 truncate" title={t.name}>{t.name}</p>
                        </div>
                        {active && <span className="absolute top-1 right-1 bg-[#D35400] text-white rounded-full p-0.5"><Check size={10} /></span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
